import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PlansDownloadCta from '../components/PlansDownloadCta';
import PlanDetailSkeleton from '../components/skeletons/PlanDetailSkeleton';
import { useSeoMeta, truncateDescription } from '../hooks/useSeoMeta';
import { useJsonLd } from '../hooks/useJsonLd';
import { planDetailPageJsonLd } from '../seo/jsonLd';
import { getPlanImageSources, planImageAbsoluteUrl } from '../data/planPhotos';
import { getPlanBySlug } from '../api/client';
import { APP_STORE_URL, PLAY_STORE_URL } from './home-redesign/constants';
import { OG_IMAGE_URL } from './home-redesign/cityLinks';
import type { PlanePublic } from '../api/types';

const cardBorder = 'border-[rgba(74,33,71,0.1)]';

const backLinkClass = `inline-flex items-center rounded-full border ${cardBorder} bg-white px-5 py-2.5 font-jakarta text-sm font-semibold text-[#2a0f2a] transition-colors hover:border-chisme-primary/30 hover:text-chisme-primary`;

const PlaneDetail: React.FC = () => {
  const { planSlug } = useParams<{ planSlug: string }>();
  const navigate = useNavigate();
  const [plane, setPlane] = useState<PlanePublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const cityDisplayName = plane
    ? plane.citySlug
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ')
    : '';

  const shareImage = plane
    ? planImageAbsoluteUrl(
        getPlanImageSources({
          slug: plane.slug,
          name: plane.name,
          photoId: plane.photoId,
          imagePosition: plane.imagePosition,
        }).src,
      )
    : OG_IMAGE_URL;

  useSeoMeta({
    title: plane
      ? `${plane.name} - Planes en ${cityDisplayName} | Chisme`
      : 'Plan - Chisme',
    description: plane
      ? truncateDescription(
          `${plane.name} en ${plane.location}, ${cityDisplayName}. ${plane.apuntadas} personas apuntadas. Únete a través de la app Chisme.`,
        )
      : 'Descubre planes y eventos con Chisme.',
    url: plane
      ? `https://chisme.social/${plane.slug}`
      : `https://chisme.social/${planSlug ?? ''}`,
    image: shareImage,
    locale: 'es',
  });

  const jsonLd = useMemo(() => {
    if (!plane) return null;
    return planDetailPageJsonLd({
      slug: plane.slug,
      name: plane.name,
      date: plane.date,
      location: plane.location,
      citySlug: plane.citySlug,
      cityName: cityDisplayName,
      apuntadas: plane.apuntadas,
      creatorName: plane.creatorName,
      imageUrl: shareImage,
    });
  }, [plane, cityDisplayName, shareImage]);
  useJsonLd(jsonLd);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);

    const slug = planSlug ?? '';
    getPlanBySlug(slug)
      .then(({ plan: found }) => {
        if (!active) return;
        if (found.isExpired) {
          navigate(`/ciudades/${found.citySlug}`);
          return;
        }
        setPlane(found);
      })
      .catch((error: Error & { status?: number }) => {
        if (!active) return;
        if (error.status === 404) {
          setNotFound(true);
          return;
        }
        console.error('Failed to load plan:', error);
        setNotFound(true);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [planSlug, navigate]);

  const handleJoinPlan = () => {
    if (!plane) return;
    const userAgent = navigator.userAgent || navigator.vendor;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /android/i.test(userAgent);

    if (isIOS || isAndroid) {
      window.location.href = `chisme://${plane.slug}`;
      setTimeout(() => {
        window.location.href = isIOS ? APP_STORE_URL : PLAY_STORE_URL;
      }, 1500);
    } else {
      window.open(APP_STORE_URL, '_blank');
    }
  };

  if (loading) {
    return (
        <div className="container-chisme py-10 lg:py-16">
          <div className="mb-8 h-10 w-56 rounded-full bg-[#ebe4ea]" aria-hidden />
          <PlanDetailSkeleton />
        </div>
    );
  }

  if (notFound || !plane) {
    return (
        <div className="container-chisme py-16 text-center lg:py-24">
          <h1 className="font-instrument text-4xl leading-tight tracking-[-0.02em] text-[#2a0f2a] sm:text-5xl">
            Plan no encontrado
          </h1>
          <p className="mx-auto mt-4 max-w-md font-jakarta text-lg text-[#5a3158]">
            Este plan no existe o ya no está disponible
          </p>
          <Link to="/ciudades" className={`${backLinkClass} mt-8`}>
            ← Ver ciudades
          </Link>
        </div>
    );
  }

  const hero = getPlanImageSources({
    slug: plane.slug,
    name: plane.name,
    photoId: plane.photoId,
    imagePosition: plane.imagePosition,
  });
  const date = new Date(plane.date);
  const fechaLine = date.toLocaleDateString('es-ES', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  const fechaCapitalized = fechaLine.charAt(0).toUpperCase() + fechaLine.slice(1);

  return (
      <div className="container-chisme py-10 lg:py-16">
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <Link to={`/ciudades/${plane.citySlug}`} className={backLinkClass}>
            ← Todos los planes en {cityDisplayName}
          </Link>
        </div>

        <div className="mx-auto max-w-xl">
          <article className={`overflow-hidden rounded-3xl border ${cardBorder} bg-white`}>
            <div className="relative aspect-[16/10] w-full shrink-0 bg-[#ebe4ea]">
              <img
                src={hero.src}
                srcSet={hero.srcSet}
                sizes="(max-width: 768px) 100vw, 576px"
                alt={plane.name}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                style={{ objectPosition: hero.imagePosition }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>

            <div className="p-5 sm:p-6">
              <header className="space-y-1">
                <h1 className="font-jakarta text-xl font-bold leading-tight tracking-tight text-[#2a0f2a] sm:text-2xl">
                  {plane.name}
                </h1>
                <p className="pt-0.5 font-jakarta text-sm leading-snug text-[#5a3158]">
                  Creado por <span className="text-[#2a0f2a]">{plane.creatorName}</span> ({plane.creatorAge})
                </p>
              </header>

              <div className={`mt-4 overflow-hidden rounded-2xl border ${cardBorder} bg-chisme-bg sm:mt-5`}>
                <div className="grid grid-cols-2 gap-0 border-b border-[rgba(74,33,71,0.1)]">
                  <div className="border-r border-[rgba(74,33,71,0.1)] px-3 py-3 text-left sm:px-4 sm:py-3.5">
                    <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
                      Fecha
                    </p>
                    <p className="mt-1 break-words font-jakarta text-xs font-bold leading-tight text-[#2a0f2a] sm:text-sm">
                      {fechaCapitalized}
                    </p>
                  </div>
                  <div className="px-3 py-3 text-left sm:px-4 sm:py-3.5">
                    <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
                      Apuntadas
                    </p>
                    <p className="mt-1 font-jakarta text-sm font-bold tabular-nums text-[#2a0f2a]">
                      {plane.apuntadas}
                    </p>
                  </div>
                </div>
                <div className="px-3 py-3 sm:px-4 sm:py-3.5">
                  <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
                    Barrio
                  </p>
                  <p className="mt-1 break-words font-jakarta text-sm font-bold leading-snug text-[#2a0f2a]">
                    {plane.location}
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={handleJoinPlan}
                  className="w-full rounded-full bg-chisme-primary px-6 py-4 font-jakarta text-base font-bold text-white transition-transform duration-200 hover:scale-[1.02] active:scale-[0.99]"
                >
                  Unirme al plan
                </button>
                <p className="mt-3 text-center font-jakarta text-xs text-[#5a3158]">
                  Te redirigiremos a la app para unirte
                </p>
              </div>
            </div>
          </article>
        </div>

        <PlansDownloadCta
          title="¿Aún no tienes la app?"
          body="Descarga Chisme y únete a planes con mujeres como tú"
        />
      </div>
  );
};

export default PlaneDetail;
